'use client'

// eslint-disable-next-line custom-rules/no-next-link
import Link from 'next/link'
import { ReadonlyURLSearchParams, usePathname, useSearchParams } from 'next/navigation'

import { ChevronLeft, ChevronRight, DoubleChevronLeft } from '@components/icons'

import s from './pagination.module.scss'

type PaginationProps = {
  totalPages: number
  pagesToDisplay?: number
}

const getPageUrl = (path: string, searchParams: ReadonlyURLSearchParams, page: number): string => {
  const newSearchparams = new URLSearchParams(searchParams)
  newSearchparams.set('page', String(page))
  return `${path}?${newSearchparams.toString()}`
}

export default function Pagination({ totalPages }: PaginationProps): JSX.Element {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const currentPage = Number(searchParams.get('page') || 1)

  /**
   * https://sonicjobs.atlassian.net/browse/CAN-257?focusedCommentId=25028
   */
  const adjustedTotalPages = Math.min(totalPages, 100)

  return (
    <div className={s.pagination}>
      {currentPage > 2 && (
        <Link className={s.icon} href={getPageUrl(pathname, searchParams, 1)} aria-label="Go to first page">
          <DoubleChevronLeft className={s.icon} />
        </Link>
      )}
      {currentPage > 1 && (
        <Link
          className={s.icon}
          href={getPageUrl(pathname, searchParams, currentPage - 1)}
          aria-label="Go to previous page"
        >
          <ChevronLeft className={s.icon} />
        </Link>
      )}
      <div className={s.inner}>
        <span>{currentPage}</span>
        <span>/</span>
        <span>{adjustedTotalPages}</span>
      </div>
      {currentPage < adjustedTotalPages && (
        <Link
          className={s.icon}
          href={getPageUrl(pathname, searchParams, currentPage + 1)}
          aria-label="Go to next page"
        >
          <ChevronRight className={s.icon} />
        </Link>
      )}
    </div>
  )
}
